import {
  AUTH_ERRORS,
  AuthProvider,
  EMPTY_USER,
  IAuthProviderProps,
  UNAUTHORIZED,
} from 'icerockdev-admin-toolkit';
import { action, computed, flow, observable, reaction } from 'mobx';
import { Unwrap } from 'icerockdev-admin-toolkit/dist/application/types/common';

export type IOAuth2SendTokenFn = (
  code: string,
  state: string
) => Promise<{
  user: IAuthProviderProps['user'];
  tokens: { access: string; refresh: string };
  error: string;
}>;

export type IOAuth2TokenRefreshFn = (
  refresh: string
) => Promise<{
  access: string;
  refresh: string;
}>;

export const EMPTY_TOKENS = {
  access: '',
  refresh: '',
};

export type IOAuth2AuthProviderProps = IAuthProviderProps & {
  authRequestFn: IOAuth2SendTokenFn;
  tokenRefreshFn: IOAuth2TokenRefreshFn;
};

export default class OAuth2AuthProvider extends AuthProvider {
  @observable tokens: Record<string, string> = EMPTY_TOKENS;
  @observable authRequestFn?: IOAuth2AuthProviderProps['authRequestFn'];
  @observable tokenRefreshFn?: IOAuth2AuthProviderProps['tokenRefreshFn'];

  constructor(fields?: Partial<IOAuth2AuthProviderProps>) {
    super(fields);

    if (this.persist) {
      const { user, tokens } = this.getPersistedCredentials();

      if (user) {
        this.user = user;
      }

      if (tokens) {
        this.tokens = tokens;
      }

      reaction(() => this.tokens, this.persistTokens);
      reaction(() => this.user, this.persistCredentials);
    }
  }

  sendAuthOAuthRequestInstance?: any;

  @action
  sendAuthOAuthRequest = async (code: string, state: string) => {
    this.sendAuthOAuthRequestInstance?.cancel();

    this.sendAuthOAuthRequestInstance = flow(function* sendAuthOAuthRequest(
      this: OAuth2AuthProvider
    ) {
      if (!this.authRequestFn) return;

      this.isLoading = true;

      try {
        const response: Unwrap<typeof this.authRequestFn> = yield this.authRequestFn(code, state);

        if (!response || response.error) {
          this.parent?.notifications.showError(response?.error || AUTH_ERRORS.CANT_LOGIN);
          throw new Error(response?.error);
        }

        this.parent?.notifications.hideNotification();
        this.user = response.user;
        this.tokens = response.tokens;
      } catch (e) {
        this.error = e;
      } finally {
        this.isLoading = false;
      }
    }).bind(this)();
  };

  @action
  logout = () => {
    this.user = EMPTY_USER;
    this.tokens = EMPTY_TOKENS;
  };

  tokenRefreshInstanceInstance?: any;

  @action
  withToken = async (req: any, args: any) => {
    const result = await req({
      ...args,
      token: `Bearer ${this.tokens.access}`,
    });

    if (result?.error === UNAUTHORIZED) {
      if (this.tokenRefreshFn) {
        if (!this.tokenRefreshInstanceInstance) {
          this.tokenRefreshInstanceInstance = flow(function* (this: OAuth2AuthProvider) {
            if (!this.tokenRefreshFn) return { access: '', refresh: '' };

            return yield this.tokenRefreshFn(this.tokens.refresh);
          }).bind(this)();

          const tokens = await this.tokenRefreshInstanceInstance;

          this.tokens = {
            access: tokens.access || '',
            refresh: tokens.refresh,
          };

          this.tokenRefreshInstanceInstance = null;
        } else {
          await this.tokenRefreshInstanceInstance;
        }

        if (this.tokens.access && this.tokens.refresh) {
          return await req({
            ...args,
            token: `Bearer ${this.tokens.access}`,
          });
        }

        this.logout();
      }
    }

    return result;
  };

  getPersistedCredentials = (): {
    user?: IAuthProviderProps['user'];
    tokens?: Record<string, string>;
  } => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const tokens = JSON.parse(localStorage.getItem('tokens') || '{}');

      if (typeof user != 'object') return {};

      return { user, tokens };
    } catch (e) {
      return {};
    }
  };

  persistCredentials = () => {
    localStorage.setItem('user', JSON.stringify(this.user));
  };

  persistTokens = () => {
    localStorage.setItem('tokens', JSON.stringify(this.tokens));
  };

  @computed
  get isLogged() {
    return !!this.tokens.access;
  }
}
